import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import CoachIdentityList from './CoachIdentityList';
import { getCoachIdentityReview } from '../services/coachIdentityApi';
import {
  CLASS_AUDIT_FILTERS,
  COACH_ATTRIBUTION_FILTERS,
  EVALUATION_REVIEW_FILTERS,
  DEFAULT_CLASS_AUDIT_FILTER,
  DEFAULT_COACH_ATTRIBUTION_FILTER,
  DEFAULT_EVALUATION_REVIEW_FILTER,
  matchesClassAuditFilter,
  matchesCoachAttributionFilter,
  matchesEvaluationReviewFilter,
} from '../utils/coachAuditProgress';

// Coach Check-In Audit — Identity Review (season-manager doc 107, slice 1).
//
// 🔑 THE GRAIN IS THE IDENTITY, AND THAT IS THE FEATURE. One row per franchise-bounded provisional
// coach identity, (com_code, typed_name_norm). Measured 2026-09-22: 523 identities needing review
// cover 1,489 evaluations, and an accepted decision also covers every future evaluation that arrives
// under the same identity — so the queue shrinks rather than refilling. An evaluation-grain queue
// would be ~4,900 decisions with no such leverage; that is slice 2's targeted tool, not this.
//
// ──────────────────────────────────────────────────────────────── why the filtering split is here
// The ENTIRE provisional population is ~2,400 identities, an order of magnitude under the server's
// 5,000-row cap, so this component fetches it once and filters client-side. That makes every filter
// COMPLETE rather than a filter over a truncated page — the failure mode getClassAuditRows has to
// work around because its population is 1.8M classes. The server still accepts com_code and search;
// they stay unused until the population outgrows the cap, at which point the fetch, not the filters,
// is what must change.

// ──────────────────────────────────────────────────────────── three facts, three filters (Part A)
// Identity rule answers "is there a durable decision for this name?" and nothing else. Whether this
// identity's evaluations still need work is Coach attribution and Class audit, each with its own
// filter. The working queue is therefore driven by Coach attribution, NOT by Identity rule: an
// identity whose every evaluation already has a coach leaves the queue without anyone pretending an
// identity rule exists, and returns on its own when an unresolved evaluation arrives.
const REVIEW_STATES = [
  { key: 'all', label: 'All' },
  { key: 'needs_review_new', label: 'No rule' },
  { key: 'needs_review_changed', label: 'Rule changed — review' },
  { key: 'accepted', label: 'Rule accepted' },
];

const CATEGORIES = [
  { key: 'all', label: 'All categories' },
  { key: 'priority', label: 'Priority collision' },
  { key: 'dash_ambiguous_review', label: 'Ambiguous' },
  { key: 'dash_name_differs_review', label: 'Name differs' },
  { key: 'typed_only_dash_not_configured', label: 'Not configured in Dash' },
  { key: 'typed_only_no_dash_roster', label: 'No Dash roster' },
];

const CANDIDATE_BUCKETS = [
  { key: 'all', label: 'Any candidates' },
  { key: 'one', label: 'Exactly one eligible' },
  { key: 'many', label: 'Multiple eligible' },
  { key: 'none', label: 'No candidate' },
];

export default function CoachCheckinAudit() {
  const [comCode, setComCode] = useState('');
  const [reviewState, setReviewState] = useState('all');
  const [coachFilter, setCoachFilter] = useState(DEFAULT_COACH_ATTRIBUTION_FILTER);
  const [classFilter, setClassFilter] = useState(DEFAULT_CLASS_AUDIT_FILTER);
  // The working queue: identities that still have an evaluation without an explicit human conclusion.
  const [reviewFilter, setReviewFilter] = useState(DEFAULT_EVALUATION_REVIEW_FILTER);
  const [category, setCategory] = useState('all');
  const [candidateBucket, setCandidateBucket] = useState('all');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [orphanedResolutions, setOrphanedResolutions] = useState(0);
  const [integrationPending, setIntegrationPending] = useState(true);

  // Only the newest response may apply, so a slow earlier request can never overwrite a newer one.
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    getCoachIdentityReview()
      .then((res) => {
        if (mine !== seq.current) return;
        setRows(res.rows || []);
        setCandidates(res.candidates || []);
        setTruncated(Boolean(res.truncated));
        setOrphanedResolutions(Number(res.orphaned_resolution_count) || 0);
        // Absent means the older contract, in which nothing reached reporting.
        setIntegrationPending(res.reporting_integration_pending !== false);
      })
      .catch((err) => {
        if (mine !== seq.current) return;
        setError(err.message || 'Could not load the identity review list.');
        setRows([]);
        setCandidates([]);
      })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, []);

  const candidatesByIdentity = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => {
      const key = `form:${c.com_code}:${c.typed_name_norm}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return map;
  }, [candidates]);

  const franchiseOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.com_code).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (comCode && r.com_code !== comCode) return false;

      if (reviewState !== 'all') {
        // 'accepted' covers both accepted states: pending-build is a timing fact about the
        // warehouse, not a different decision, and splitting them here would hide settled work.
        const matches = reviewState === 'accepted'
          ? (r.review_state === 'accepted' || r.review_state === 'accepted_pending_build')
          : r.review_state === reviewState;
        if (!matches) return false;
      }

      if (!matchesEvaluationReviewFilter(r, reviewFilter)) return false;
      if (!matchesCoachAttributionFilter(r, coachFilter)) return false;
      if (!matchesClassAuditFilter(r, classFilter)) return false;

      if (category === 'priority') {
        if (r.is_priority_audit !== 1) return false;
      } else if (category !== 'all' && r.review_category !== category) {
        return false;
      }

      if (candidateBucket === 'one' && r.eligible_candidate_count !== 1) return false;
      if (candidateBucket === 'many' && !(r.eligible_candidate_count > 1)) return false;
      if (candidateBucket === 'none' && r.candidate_count !== 0) return false;

      if (term && !String(r.form_raw_name || '').toLowerCase().includes(term)) return false;
      return true;
    });
  }, [rows, comCode, reviewState, reviewFilter, coachFilter, classFilter, category, candidateBucket, search]);

  const pendingCount = useMemo(
    () => rows.filter((r) => r.pending_rebuild === true).length,
    [rows],
  );

  // An accepted rule whose coach has vanished from Dash has no evaluation left needing review, so the
  // default Coach attribution filter would hide it. It is surfaced here instead, always.
  const changedRuleCount = useMemo(
    () => rows.filter((r) => r.review_state === 'needs_review_changed').length,
    [rows],
  );
  const showChangedRules = () => {
    setReviewState('needs_review_changed');
    setReviewFilter('any');
    setCoachFilter('any');
    setClassFilter('any');
  };

  // "Saved ✓" for an evaluation review, at PAGE level: the identity it belongs to may close, or leave
  // the filtered grid, in the same instant. `seq` restarts the timer for back-to-back saves.
  const SAVED_NOTICE_MS = 2500;
  const [savedNotice, setSavedNotice] = useState({ text: '', seq: 0, open: false });
  const handleEvaluationSaved = (text) => setSavedNotice((prev) => ({ text, seq: prev.seq + 1, open: true }));

  // Merge a written row in place so an Add/Update updates the grid immediately. The merge re-runs the
  // filters: an identity whose last evaluation was just reviewed leaves a "Has unreviewed" view at once.
  const handleRowUpdate = (updated) => {
    if (!updated || !updated.reporting_coach_key) return;
    setRows((prev) => prev.map((r) => (
      r.reporting_coach_key === updated.reporting_coach_key ? { ...r, ...updated } : r
    )));
  };

  const franchiseItems = franchiseOptions.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>);
  const stateItems = REVIEW_STATES.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>);
  const coachItems = COACH_ATTRIBUTION_FILTERS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>);
  const reviewItems = EVALUATION_REVIEW_FILTERS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>);
  const classItems = CLASS_AUDIT_FILTERS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>);
  const categoryItems = CATEGORIES.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>);
  const bucketItems = CANDIDATE_BUCKETS.map((b) => <MenuItem key={b.key} value={b.key}>{b.label}</MenuItem>);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        One row per Form coach identity: a franchise and the coach name typed on the Form. Three
        separate things are shown for each, and none implies another.
      </Typography>
      <Typography variant="body2" color="text.secondary" component="ul" sx={{ mb: 0.5, pl: 3 }}>
        <li><strong>Identity rule</strong> — a decision for this name that applies to every current and future evaluation under it.</li>
        <li><strong>Evaluation review</strong> — evaluations an auditor has concluded one by one: a class outcome and a coach outcome, even when that is &ldquo;no Dash class&rdquo; and &ldquo;keep the Form coach&rdquo;.</li>
        <li><strong>Class audit</strong> — evaluations with a Dash class recorded.</li>
        <li><strong>Coach attribution</strong> — evaluations that have a usable reporting coach as of the last warehouse build.</li>
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
        Counts cover the evaluations under review for this name. Evaluations under the same name that
        Dash already validates are listed when expanded but not counted. Class audit excludes
        evaluations with no Dash franchise (for example Sofive), which have no Dash class to match.
        Franchise-training check-ins are never included.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="ci-franchise">Franchise</InputLabel>
          <Select labelId="ci-franchise" label="Franchise" value={comCode} onChange={(e) => setComCode(e.target.value)}>
            <MenuItem value="">All franchises</MenuItem>
            {franchiseItems}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel id="ci-state">Identity rule</InputLabel>
          <Select labelId="ci-state" label="Identity rule" value={reviewState} onChange={(e) => setReviewState(e.target.value)}>
            {stateItems}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="ci-review">Evaluation review</InputLabel>
          <Select labelId="ci-review" label="Evaluation review" value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)}>
            {reviewItems}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel id="ci-coach">Coach attribution</InputLabel>
          <Select labelId="ci-coach" label="Coach attribution" value={coachFilter} onChange={(e) => setCoachFilter(e.target.value)}>
            {coachItems}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="ci-class">Class audit</InputLabel>
          <Select labelId="ci-class" label="Class audit" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
            {classItems}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel id="ci-category">Category</InputLabel>
          <Select labelId="ci-category" label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {categoryItems}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="ci-candidates">Candidates</InputLabel>
          <Select labelId="ci-candidates" label="Candidates" value={candidateBucket} onChange={(e) => setCandidateBucket(e.target.value)}>
            {bucketItems}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Coach name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Box>

      {pendingCount > 0 && (
        // Self-correcting: derived fresh from the server's comparison of each decision against the
        // audit relation's last build, never persisted client-side, so it clears when a build lands.
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {pendingCount} identity rule{pendingCount === 1 ? '' : 's'} pending the next warehouse build.
          CP-06 and CP-08 reflect them after that build.
        </Alert>
      )}

      {changedRuleCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={showChangedRules}>Show them</Button>}
        >
          {changedRuleCount} identity rule{changedRuleCount === 1 ? ' needs' : 's need'} review because
          the accepted coach changed.
        </Alert>
      )}

      {orphanedResolutions > 0 && (
        // A reconciliation carries no name, so one whose evaluation left the audit relation cannot be
        // shown under any identity. Counted here rather than silently dropped; nothing is deleted.
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {orphanedResolutions} recorded class reconciliation{orphanedResolutions === 1 ? ' refers' : 's refer'} to
          evaluations no longer in the audit. {orphanedResolutions === 1 ? 'It is' : 'They are'} kept, but not
          counted under any identity.
        </Alert>
      )}

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The result was capped, so these filters are incomplete. Narrow by franchise.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            {visibleRows.length} of {rows.length} identities
          </Typography>
          <CoachIdentityList
            rows={visibleRows}
            candidatesByIdentity={candidatesByIdentity}
            onRowUpdate={handleRowUpdate}
            onEvaluationSaved={handleEvaluationSaved}
            integrationPending={integrationPending}
          />
        </>
      )}

      {/* The ACCESSIBLE announcement. A live region is only reliable if it exists before its text
          changes, so it is always rendered, and never inside anything that can unmount. */}
      <div role="status" aria-live="polite" className="visually-hidden">{savedNotice.text}</div>
      {/* The VISIBLE confirmation: duplicates the live region, so it is hidden from assistive tech. */}
      <Snackbar
        key={savedNotice.seq}
        open={savedNotice.open}
        autoHideDuration={SAVED_NOTICE_MS}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSavedNotice((prev) => ({ ...prev, open: false })); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="success" variant="filled" role="presentation" aria-hidden="true" sx={{ fontWeight: 500 }}>
          {savedNotice.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
